import { useState, useCallback, useMemo } from 'react';
import { CalendarEvent, CalendarLocation, DossierData, TemplateOptions } from '../types';

interface UseCalendarDataOptions {
    data: DossierData | null;
    templateOptions: TemplateOptions | null;
}

interface UseCalendarDataResult {
    events: CalendarEvent[];
    locations: CalendarLocation[];
    currentDay: Date;
    nextDay: Date;
    dayAfterNext: Date;
    incrementDay: () => void;
    decrementDay: () => void;
    getEventsForLocation: (locationName: string) => CalendarEvent[];
    isEventOnDay: (event: CalendarEvent, day: Date, type: 'start' | 'end') => boolean;
}

const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

export const useCalendarData = ({
    data,
    templateOptions,
}: UseCalendarDataOptions): UseCalendarDataResult => {
    const [dayOffset, setDayOffset] = useState(0);

    const baseDate = useMemo(() => new Date(), []);

    const currentDay = useMemo(() => addDays(baseDate, dayOffset), [baseDate, dayOffset]);
    const nextDay = useMemo(() => addDays(baseDate, dayOffset + 1), [baseDate, dayOffset]);
    const dayAfterNext = useMemo(() => addDays(baseDate, dayOffset + 2), [baseDate, dayOffset]);

    const { events, locations } = useMemo(() => {
        if (!data || !templateOptions) {
            return { events: [], locations: [] };
        }

        const options = templateOptions;
        const extractedEvents: CalendarEvent[] = [];
        const extractedLocations: CalendarLocation[] = [];
        const locationSet = new Set<number>();

        data.dossiers.forEach((dossier) => {
            const dossierContent = data.contents.filter((content) => content.id === dossier.id);

            const name = options.name
                ? dossierContent.find((field) => field.content.id === options.name)
                : null;
            const location = options.location
                ? dossierContent.find((field) => field.content.id === options.location)
                : null;
            const start = options.start
                ? dossierContent.find((field) => field.content.id === options.start)
                : null;
            const end = options.end
                ? dossierContent.find((field) => field.content.id === options.end)
                : null;

            if (
                options.name !== null &&
                options.location !== null &&
                options.start !== null &&
                options.end !== null &&
                name &&
                location &&
                start &&
                end
            ) {
                const locationContent = location.content.content as Array<{
                    id: number;
                    summary: string;
                }>;

                if (locationContent && locationContent.length > 0) {
                    extractedEvents.push({
                        id: dossier.id,
                        name: name.content.content as string,
                        start: start.content.content as string,
                        end: end.content.content as string,
                        location: locationContent[0].summary,
                    });

                    if (!locationSet.has(locationContent[0].id)) {
                        locationSet.add(locationContent[0].id);
                        extractedLocations.push({
                            id: locationContent[0].id,
                            name: locationContent[0].summary,
                        });
                    }
                }
            }
        });

        return { events: extractedEvents, locations: extractedLocations };
    }, [data, templateOptions]);

    const incrementDay = useCallback(() => {
        setDayOffset((prev) => prev + 1);
    }, []);

    const decrementDay = useCallback(() => {
        setDayOffset((prev) => prev - 1);
    }, []);

    const getEventsForLocation = useCallback(
        (locationName: string) => {
            return events.filter((event) => event.location === locationName);
        },
        [events]
    );

    const isEventOnDay = useCallback(
        (event: CalendarEvent, day: Date, type: 'start' | 'end'): boolean => {
            const eventDate = new Date(type === 'start' ? event.start : event.end);
            return formatDate(eventDate) === formatDate(day);
        },
        []
    );

    return {
        events,
        locations,
        currentDay,
        nextDay,
        dayAfterNext,
        incrementDay,
        decrementDay,
        getEventsForLocation,
        isEventOnDay,
    };
};

export const formatDayName = (date: Date, locale = 'en-US'): string => {
    return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
};

export const formatDayNumber = (date: Date): string => {
    return String(date.getDate()).padStart(2, '0');
};

export const formatMonthYear = (date: Date, locale = 'en-US'): string => {
    return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
};
